import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {map, catchError} from 'rxjs/operators';
import {DirectusTokenService} from '@app/providers/expo/directus/directus-token.service';
import {DirectusErrorService} from './directus-error.service';
import {environment} from '@env/environment';
import {ModeService} from '@app/providers/mode.service';

// NOTE: every method below passes its `catchError(...)` as the SECOND ARGUMENT
// to `map` (map's deprecated `thisArg`), not as a pipe operator — so the error
// handler never runs and Directus failures propagate to callers unprocessed.
// Left as-is on purpose: moving it into `.pipe(...)` would newly enable
// DirectusErrorService's credential wipe + redirect on every Directus error,
// a user-visible behavior change that needs its own decision. See
// docs/sentry.md ("Known bug found while instrumenting").
@Injectable({
  providedIn: 'root',
})
export class DirectusHttpService {
  private baseURL = environment.cmsDomain;

  private headers = new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });

  constructor(
    private http: HttpClient,
    private directusToken: DirectusTokenService,
    private errorService: DirectusErrorService,
    private modeService: ModeService
  ) {}

  get<T>(resource: string): Observable<T> {
    this.addAuthHeaders();
    return this.http
      .get<T>(this.baseURL + resource, {headers: this.headers})
      .pipe(
        map(
          (res) => {
            return res;
          },
          catchError((e) => {
            return throwError(() => this.errorService.onError(e));
          })
        )
      );
  }

  put<T>(resource: string, body: any): Observable<T> {
    this.addAuthHeaders();
    return this.http
      .put<T>(this.baseURL + resource + '?activity_skip=1', body, {
        headers: this.headers,
      })
      .pipe(
        map(
          (res) => {
            return res;
          },
          catchError((e) => {
            return throwError(() => this.errorService.onError(e));
          })
        )
      );
  }

  post<T>(resource: string, body: any): Observable<T> {
    this.addAuthHeaders();
    if (resource.includes('login') || resource.includes('refresh')) {
      this.removeAuthHeader();
    }
    return this.http
      .post<T>(this.baseURL + resource + '?activity_skip=1', body, {
        headers: this.headers,
      })
      .pipe(
        map(
          (res) => {
            return res;
          },
          catchError((e) => {
            return throwError(() => this.errorService.onError(e));
          })
        )
      );
  }

  patch<T>(resource: string, body: any): Observable<T> {
    this.addAuthHeaders();
    return this.http
      .patch<T>(this.baseURL + resource + '?activity_skip=1', body, {
        headers: this.headers,
      })
      .pipe(
        map(
          (res) => {
            return res;
          },
          catchError((e) => {
            return throwError(() => this.errorService.onError(e));
          })
        )
      );
  }

  delete<T>(resource: string): Observable<T> {
    this.addAuthHeaders();
    return this.http
      .delete<T>(this.baseURL + resource, {headers: this.headers})
      .pipe(
        map(
          (res) => {
            return res;
          },
          catchError((e) => {
            return throwError(() => this.errorService.onError(e));
          })
        )
      );
  }

  private addAuthHeaders() {
    if (this.modeService.getStoredMode() === 'cks') {
      this.headers = this.headers.set(
        'Authorization',
        'Bearer ' + environment.kioskUserKey
      );
    } else {
      if (this.directusToken.isAuthed()) {
        this.headers = this.headers.set(
          'Authorization',
          'Bearer ' + this.directusToken.authToken
        );
      }
    }
  }

  private removeAuthHeader() {
    this.headers = this.headers.delete('Authorization');
  }
}
